import { z } from 'zod';
import { stageSchema } from './common.ts';

/**
 * Environment variables for each runtime, validated once at start-up (cold start).
 * A missing or malformed variable fails fast with a clear message instead of a mystery bug later.
 */

/** How to reach the database. Local = PGlite, CI integration = node-postgres, AWS = RDS Data API. */
const dbEnv = {
  DB_DRIVER: z.enum(['pglite', 'pg', 'data-api']),
  /** pglite: directory for the local database (omit for in-memory). */
  BNB_PGLITE_DIR: z.string().optional(),
  /** pg: postgres:// connection string (CI only). */
  DATABASE_URL: z.string().optional(),
  /** data-api: Aurora cluster ARN, Secrets Manager secret ARN for the DB user, database name. */
  DB_CLUSTER_ARN: z.string().optional(),
  DB_SECRET_ARN: z.string().optional(),
  DB_NAME: z.string().optional(),
};

/**
 * Generic integration plumbing (Shopify + QuickBooks share it). All optional: absent until the
 * integrations construct is enabled in infra, and absent on a laptop (no queues locally).
 */
const integrationEnv = {
  /** Standard SQS queue (+ DLQ) for inbound triggers (webhook doorbells, CDC changes). */
  INTEGRATION_INBOUND_QUEUE_URL: z.string().optional(),
  /** FIFO SQS queue (+ DLQ) for outbound writes, one MessageGroupId per connected account. */
  INTEGRATION_OUTBOUND_QUEUE_URL: z.string().optional(),
  /**
   * Public https origin of the web app (e.g. https://app.example.com). OAuth redirect URIs
   * (/integrations/<system>/callback) and links in emails are built from it.
   */
  WEB_BASE_URL: z.url({ protocol: /^https?$/ }).optional(),
};

function requireDbFields(
  env: {
    DB_DRIVER: string;
    DATABASE_URL?: string | undefined;
    DB_CLUSTER_ARN?: string | undefined;
    DB_SECRET_ARN?: string | undefined;
    DB_NAME?: string | undefined;
  },
  ctx: z.RefinementCtx,
) {
  if (env.DB_DRIVER === 'pg' && !env.DATABASE_URL) {
    ctx.addIssue({
      code: 'custom',
      message: 'DATABASE_URL is required when DB_DRIVER=pg',
      path: ['DATABASE_URL'],
    });
  }
  if (env.DB_DRIVER === 'data-api') {
    for (const key of ['DB_CLUSTER_ARN', 'DB_SECRET_ARN', 'DB_NAME'] as const) {
      if (!env[key])
        ctx.addIssue({
          code: 'custom',
          message: `${key} is required when DB_DRIVER=data-api`,
          path: [key],
        });
    }
  }
}

export const apiEnvSchema = z
  .object({
    STAGE: stageSchema,
    APP_VERSION: z.string().default('dev'),
    ...dbEnv,
    /** AppSync Events HTTP endpoint host (no scheme), e.g. abc.appsync-api.us-west-2.amazonaws.com. Absent locally. */
    LIVE_HTTP_ENDPOINT: z.string().optional(),
    NOTIFY_QUEUE_URL: z.string().optional(),
    /** Oldest web build the API still accepts (barn phones may run old code). */
    MIN_CLIENT_BUILD: z.string().optional(),
    AWS_REGION: z.string().optional(),
    ...integrationEnv,
  })
  .superRefine(requireDbFields);
export type ApiEnv = z.infer<typeof apiEnvSchema>;

export const workersEnvSchema = z
  .object({
    STAGE: stageSchema,
    APP_VERSION: z.string().default('dev'),
    ...dbEnv,
    NOTIFY_QUEUE_URL: z.string().optional(),
    /** Verified SES sender, e.g. "BNB Planner <alerts@barenakedbirdies.com>". */
    SES_FROM_ADDRESS: z.string().optional(),
    /** Comma-separated list of who gets operational alerts. */
    ALERT_EMAILS: z.string().optional(),
    NWS_STATION: z.string().default('KLHM'),
    NWS_FALLBACK_STATION: z.string().default('KSMF'),
    NWS_USER_AGENT: z.string().default('bnb-planner (ops@barenakedbirdies.com)'),
    AWS_REGION: z.string().optional(),
    ...integrationEnv,
  })
  .superRefine(requireDbFields);
export type WorkersEnv = z.infer<typeof workersEnvSchema>;

const SECRET_ARN = /^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/;

/**
 * The database migrator Lambda (`bnb-<stage>-migrate`, packages/db src/aws/migrator.ts), set by
 * infra `migratorEnvironment`. DB_SECRET_ARN is the MASTER login (migrations need DDL rights);
 * DB_APP_SECRET_ARN is the bnb_app login it creates/updates. Staging and prod only.
 */
export const migratorEnvSchema = z.object({
  STAGE: z.string().min(1, 'STAGE is required').pipe(stageSchema),
  APP_VERSION: z.string().min(1, 'APP_VERSION is required'),
  DB_DRIVER: z.literal('data-api', { error: 'DB_DRIVER must be data-api' }),
  DB_CLUSTER_ARN: z.string({ error: 'DB_CLUSTER_ARN is required' }).min(1),
  DB_SECRET_ARN: z
    .string({ error: 'DB_SECRET_ARN is required' })
    .regex(SECRET_ARN, 'DB_SECRET_ARN must be a Secrets Manager ARN'),
  DB_APP_SECRET_ARN: z
    .string({ error: 'DB_APP_SECRET_ARN is required' })
    .regex(SECRET_ARN, 'DB_APP_SECRET_ARN must be a Secrets Manager ARN'),
  DB_NAME: z.string({ error: 'DB_NAME is required' }).min(1),
});
export type MigratorEnv = z.infer<typeof migratorEnvSchema>;

/**
 * Runtime config the web app loads from /config.json at start-up.
 * In AWS the deploy writes it next to index.html; locally the dev API serves it.
 * One web build therefore works in every stage (build once, promote the same bytes).
 */
export const webRuntimeConfigSchema = z.object({
  stage: stageSchema,
  version: z.string(),
  region: z.string().optional(),
  auth: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('dev-personas') }),
    z.object({ mode: z.literal('cognito'), userPoolId: z.string(), userPoolClientId: z.string() }),
  ]),
  live: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('sse'), url: z.string() }),
    z.object({
      mode: z.literal('appsync'),
      httpEndpoint: z.string(),
      realtimeEndpoint: z.string(),
      namespace: z.string(),
    }),
    z.object({ mode: z.literal('off') }),
  ]),
});
export type WebRuntimeConfig = z.infer<typeof webRuntimeConfigSchema>;
